import {findAddedReference, isCitationAlreadyAdded} from './buildCitationFromEvidence';
import {buildSupportingEvidenceRows, getEvidenceDedupeKey} from './buildSupportingEvidenceRows';
import {getCoverageForClaim} from './getClaimCoverage';

/**
 * Added references cited as supporting evidence (at least partially prove the claim).
 *
 * @param {Object} claim
 * @param {Array} references
 * @returns {number}
 */
export function getClaimSupportingCount(claim, references = []) {
    return buildSupportingEvidenceRows(claim, references)
        .filter((row) => findAddedReference(row.citation, references))
        .length;
}

/**
 * @param {Object} citation
 * @param {string|number} claimId
 * @returns {boolean} true when the citation is relevant to this claim.
 *
 * Backend V4 suggestions cover many claims at once and carry `claimCoverage`/
 * `forClaimIds` scoping metadata — for those, relevance is determined by
 * {@link getCoverageForClaim}. Citations without any such scoping metadata
 * (e.g. per-claim client search hits, already filtered by the caller to a
 * single claim) are assumed relevant by default rather than excluded.
 */
function isRelevantToClaim(citation, claimId) {
    const hasScopeMetadata = Array.isArray(citation?.claimCoverage) || Array.isArray(citation?.forClaimIds);
    if (!hasScopeMetadata) return true;
    return getCoverageForClaim(citation, claimId) != null;
}

/**
 * V4 backend suggestions for a claim (Y.Doc suggestedReferences), excluding
 * passages already in addedReferences, plus a fallback scan of the claim's own
 * discovery-corpus evidence (`supporting_evidence` rows referencing a corpus
 * passage that hasn't been added yet) — the same `buildSupportingEvidenceRows`
 * this module already uses for {@link getClaimSupportingCount}.
 *
 * @param {Object} claim
 * @param {Array} references
 * @param {Array} [suggestedReferences] Backend V4 suggestions
 * @returns {number}
 */
export function getClaimSuggestedCount(claim, references = [], suggestedReferences = []) {
    if (!claim) return 0;
    const seen = new Set();
    let count = 0;

    (Array.isArray(suggestedReferences) ? suggestedReferences : []).forEach((citation) => {
        if (!citation || isCitationAlreadyAdded(citation, references)) return;
        if (!isRelevantToClaim(citation, claim.id)) return;
        const key = getEvidenceDedupeKey(citation, citation.referenceId || citation.localId || '');
        if (seen.has(key)) return;
        seen.add(key);
        count += 1;
    });

    buildSupportingEvidenceRows(claim, references).forEach((row) => {
        if (row.reference) return; // already added -> counted as supporting, not suggested
        if (!row.citation) return;
        const key = getEvidenceDedupeKey(row.citation, row.referenceId || '');
        if (seen.has(key)) return; // already counted via explicit suggestedReferences
        seen.add(key);
        count += 1;
    });

    return count;
}
